/**
 * The five rail destinations, redrawn from the PNG artwork as vectors.
 *
 * Solid shapes on a 24 grid, matching the originals: a four-square grid for
 * Parties, a pair of figures for Friends, a shield with a cut-out tick for
 * Staff, two overlapping bubbles for Chat, and an eight-tooth gear for
 * Settings. Cut-outs use fill-rule="evenodd" rather than painting the
 * background colour over the top, so they stay transparent on any surface.
 */

export type IconProps = {
  size?: number;
  className?: string;
  title?: string;
};

function svgProps({ size = 24, className, title }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'currentColor',
    className,
    role: title ? ('img' as const) : ('presentation' as const),
    'aria-label': title,
    'aria-hidden': title ? undefined : true,
    focusable: 'false' as const
  };
}

/** Parties: four rounded tiles, the lobby grid. */
export function PartiesIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <rect x="2.6" y="2.6" width="8.2" height="8.2" rx="2.6" />
      <rect x="13.2" y="2.6" width="8.2" height="8.2" rx="2.6" />
      <rect x="2.6" y="13.2" width="8.2" height="8.2" rx="2.6" />
      <rect x="13.2" y="13.2" width="8.2" height="8.2" rx="2.6" />
    </svg>
  );
}

/** Friends: one figure forward, a second behind and to the right. */
export function FriendsIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      {/* the one behind, drawn first so the front figure overlaps it */}
      <path d="M16.9 3.1a3.6 3.6 0 0 1 0 7.2 3.6 3.6 0 0 1-1.5-.33 5.9 5.9 0 0 0 0-6.54 3.6 3.6 0 0 1 1.5-.33Z" />
      <path d="M15.6 12.1h2.1a4.3 4.3 0 0 1 4.3 4.3 2 2 0 0 1-2 2h-4.2a7 7 0 0 0-2.6-5.3 5.9 5.9 0 0 1 2.4-1Z" />
      {/* the one in front */}
      <circle cx="9.4" cy="7.2" r="4.6" />
      <path d="M9.4 13.1c3.8 0 6.9 2.6 6.9 5.9v.6a2 2 0 0 1-2 2H4.5a2 2 0 0 1-2-2V19c0-3.3 3.1-5.9 6.9-5.9Z" />
    </svg>
  );
}

/** Staff: a shield with the tick punched out of it. */
export function StaffIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)} fillRule="evenodd" clipRule="evenodd">
      <path
        d="M11.72 1.63a1 1 0 0 1 .56 0l8.3 2.5a1 1 0 0 1 .71.83c.66 5.5.02 9.72-1.86 12.85-1.86 3.1-4.83 4.94-8.1 6.02a1 1 0 0 1-.63 0c-3.28-1.08-6.25-2.92-8.11-6.02C.71 14.68.07 10.46.73 4.96a1 1 0 0 1 .7-.83l8.3-2.5ZM16.9 8.4a1 1 0 0 0-1.42 0l-4.9 4.9-2.06-2.06a1 1 0 0 0-1.42 0l-1.1 1.1a1 1 0 0 0 0 1.42l3.87 3.87a1 1 0 0 0 1.42 0l6.71-6.71a1 1 0 0 0 0-1.42l-1.1-1.1Z"
      />
    </svg>
  );
}

/** Chat: two bubbles with tails, the front one carrying two lines. */
export function ChatIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)} fillRule="evenodd" clipRule="evenodd">
      {/* the bubble behind, pointing the other way */}
      <path d="M18.4 7.3h1.3A2.3 2.3 0 0 1 22 9.6v7.1a2.3 2.3 0 0 1-2.3 2.3h-.8v2a.8.8 0 0 1-1.25.66l-3.9-2.66H8.6a2.3 2.3 0 0 1-2.3-2.3v-1.03l1.9-1.34h6.3a3.9 3.9 0 0 0 3.9-3.9V7.3Z" />
      {/* the bubble in front */}
      <path d="M2 4.3A2.3 2.3 0 0 1 4.3 2h10.2a2.3 2.3 0 0 1 2.3 2.3v7.1a2.3 2.3 0 0 1-2.3 2.3H8.1l-3.85 2.7A.8.8 0 0 1 3 15.75V13.7h-.7A2.3 2.3 0 0 1 2 11.4V4.3Zm3.6 1.9a.85.85 0 0 0 0 1.7h7.6a.85.85 0 0 0 0-1.7H5.6Zm0 3.4a.85.85 0 1 0 0 1.7h4.6a.85.85 0 0 0 0-1.7H5.6Z" />
    </svg>
  );
}

/** Settings: an eight-tooth gear with the centre punched out. */
export function SettingsIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)} fillRule="evenodd" clipRule="evenodd">
      <path
        d="M10.32 1.62a.9.9 0 0 1 .88-.72h1.6a.9.9 0 0 1 .88.72l.5 2.4a8.1 8.1 0 0 1 2.06.86l2.05-1.35a.9.9 0 0 1 1.13.12l1.13 1.13a.9.9 0 0 1 .12 1.13L19.32 7.9c.37.64.66 1.33.86 2.06l2.4.5a.9.9 0 0 1 .72.88v1.6a.9.9 0 0 1-.72.88l-2.4.5a8.1 8.1 0 0 1-.86 2.06l1.35 2.05a.9.9 0 0 1-.12 1.13l-1.13 1.13a.9.9 0 0 1-1.13.12l-2.05-1.35c-.64.37-1.33.66-2.06.86l-.5 2.4a.9.9 0 0 1-.88.72h-1.6a.9.9 0 0 1-.88-.72l-.5-2.4a8.1 8.1 0 0 1-2.06-.86l-2.05 1.35a.9.9 0 0 1-1.13-.12L3.56 19.6a.9.9 0 0 1-.12-1.13l1.35-2.05a8.1 8.1 0 0 1-.86-2.06l-2.4-.5a.9.9 0 0 1-.72-.88v-1.6a.9.9 0 0 1 .72-.88l2.4-.5c.2-.73.49-1.42.86-2.06L3.44 6.9a.9.9 0 0 1 .12-1.13l1.13-1.13a.9.9 0 0 1 1.13-.12L7.87 5.87c.64-.37 1.33-.66 2.06-.86l.5-2.4-.11-.99ZM12 7.4a4.6 4.6 0 1 0 0 9.2 4.6 4.6 0 0 0 0-9.2Z"
      />
    </svg>
  );
}

export const NAV_ICONS = {
  parties: PartiesIcon,
  friends: FriendsIcon,
  chat: ChatIcon,
  staff: StaffIcon,
  settings: SettingsIcon
} as const;

export type NavIconName = keyof typeof NAV_ICONS;
